import { ChipExportBundleSchema } from '@/schemas/chip';
import { CircuitType } from '@/schemas/common';

/** Ordinary editable chip, offered through the existing extension chip importer. */
export function createTimeChip(type: string) {
  const chipId = 'chip-system-time-48';
  const instanceId = 'instance-system-time-48';
  const signals = { top: null, right: null, bottom: null, left: null };
  const tile = (
    id: string,
    tileType: string,
    circuitType: CircuitType,
    x: number,
    y: number,
    state: unknown,
  ) => ({
    id,
    tileType,
    globalTileLocation: { chipInstanceId: instanceId, circuitType, location: { x, y } },
    meta: {
      inputs: { ...signals },
      outputs: { ...signals },
      rotation: 0,
      partOfChip: null,
    },
    state,
  });
  const bits = Array.from({ length: 48 }, (_, bit) => bit);
  const interfaceState = (bit: number, circuitType: CircuitType) => ({
    connectedInterface: {
      chipInstanceId: instanceId,
      circuitType,
      interfaceLinkId: `interface-link-time-${bit}`,
    },
    inputMode: 'none',
    toggleState: 0,
  });
  return ChipExportBundleSchema.parse({
    version: 1,
    exportedAt: '2026-10-03T00:00:00.000Z',
    rootChipId: chipId,
    chips: [
      {
        id: chipId,
        name: 'System time · 48-bit Unix seconds',
        description:
          'UTC Unix seconds. Right pins 0–23, left pins 24–47, low to high from top to bottom. Enable System time live input in Settings → Extensions.',
        color: '#5d9dad',
        logicCircuitDto: {
          id: 'circuit-time-logic',
          circuitType: CircuitType.ChipLogic,
          tileManagerDto: {
            tiles: bits.flatMap((bit) => [
              tile(`tile-time-source-${bit}`, type, CircuitType.ChipLogic, 0, bit * 2, {
                bit,
              }),
              tile(
                `tile-time-logic-${bit}`,
                'SingleInterface',
                CircuitType.ChipLogic,
                1,
                bit * 2,
                interfaceState(bit, CircuitType.ChipStructure),
              ),
            ]),
          },
        },
        structureCircuitDto: {
          id: 'circuit-time-structure',
          circuitType: CircuitType.ChipStructure,
          tileManagerDto: {
            tiles: [
              ...bits.map((bit) =>
                tile(
                  `tile-time-pin-${bit}`,
                  'SingleInterface',
                  CircuitType.ChipStructure,
                  bit < 24 ? 3 : 0,
                  bit % 24,
                  interfaceState(bit, CircuitType.ChipLogic),
                ),
              ),
              ...Array.from({ length: 24 }, (_, y) =>
                [1, 2].map((x) =>
                  tile(
                    `tile-time-body-${x}-${y}`,
                    'Structure',
                    CircuitType.ChipStructure,
                    x,
                    y,
                    {},
                  ),
                ),
              ).flat(),
            ],
          },
        },
        structureOriginLocationDto: { x: 0, y: 0 },
        interfaceLinks: bits.map((bit) => ({
          id: `interface-link-time-${bit}`,
          type: 0,
          label: String(bit),
          color: '#5d9dad',
          inputMode: 'none',
          logicTileId: `tile-time-logic-${bit}`,
          structureTileId: `tile-time-pin-${bit}`,
        })),
        chipInstanceDtos: [],
        editingInstanceDto: {
          id: instanceId,
          chipId,
          originPointDto: { x: 0, y: 0 },
          logicGraphDto: { nodes: [], edges: [] },
        },
      },
    ],
  });
}
